<script lang="ts" setup>
import { faArrowUpRightFromSquare } from "@fortawesome/pro-light-svg-icons";
import type {
    CredentialsConnection,
    OAuthConnection,
    OAuthCredentialsConnection,
} from "~/models/Connection";
import type { ApiErrorResponse } from "~/plugins/errors/apiError";
import { usePopup } from "~/composables/usePopup";
import { showKomboConnect } from "@kombo-api/connect";
import { googleSdkLoaded } from "vue3-google-login";
import type { FacebookSDK } from "~/plugins/facebook.client";

const { user } = useAuth();

const props = defineProps<{
    label: string;
    logo: string;
    uuid?: string;
    connection:
        | CredentialsConnection
        | OAuthCredentialsConnection
        | OAuthConnection;
    connected?: () => Promise<unknown>;
}>();

const emit = defineEmits<{
    (e: "connected"): void;
    (e: "error"): void;
    (e: "cancel"): void;
}>();

const toast = useToast();
const api = useApi();
const { t } = useI18n();
const fields = ref<Record<string, string>>({});
const submitting = ref<boolean>(false);
const { refreshIdentity } = useSanctumAuth();

const error = ref<ApiErrorResponse>({
    message: "",
    errors: {},
});

function processKombo() {
    api.request((props.connection as CredentialsConnection).urls.link, {
        method: "POST",
        body: {
            account_name: user.value.account.name,
        },
    })
        .then((response: any) => {
            if (response.data.link) {
                showKomboConnect(response.data.link).then(
                    (activationToken: any) => {
                        api.request(
                            (props.connection as CredentialsConnection).urls
                                .activate,
                            {
                                method: "POST",
                                body: JSON.stringify({
                                    token: activationToken,
                                }),
                            },
                        )
                            .then((response: any) => {
                                fields.value = response;
                                saveCredentials();
                            })
                            .catch((error: string) => {
                                error.value = error;
                                emit("error");
                                submitting.value = false;
                                return;
                            });
                    },
                );
            }
        })
        .catch((error: string) => {
            error.value = error;
            emit("error");
            submitting.value = false;
            return;
        });
}

function saveCredentials() {
    if (!submitting.value) {
        submitting.value = true;
        api.request((props.connection as CredentialsConnection).urls.store, {
            method: "POST",
            body: fields.value,
        })
            .then(() => {
                toast.add({
                    title: t("toasts.third_party_connected", {
                        app: props.label,
                    }),
                });

                if (props.connected) {
                    props.connected().finally(async () => {
                        submitting.value = false;
                        await refreshIdentity();
                        emit("connected");
                    });
                } else {
                    emit("connected");
                    submitting.value = false;
                }
            })
            .catch((err: ApiErrorResponse) => {
                error.value = err;
                emit("error");
                submitting.value = false;
            });
    }
}

const interval = ref();

function oAuthConnect(
    url: string,
    key: string,
    params: Record<string, string> = {},
) {
    if (key === "facebook" || key === "facebook_leads") {
        submitting.value = true;

        (window as unknown as { FB: FacebookSDK }).FB.login(
            function (response) {
                if (response.authResponse) {
                    useApi()
                        .third_party.callback(key, {
                            current_url: location.href,
                            ...response.authResponse,
                        })
                        .then(() => {
                            if (props.connected) {
                                props.connected();
                            }
                            emit("connected");
                            submitting.value = false;
                        })
                        .catch(() => {
                            emit("error");
                        })
                        .finally(() => {
                            submitting.value = false;
                        });
                } else if (response.status === "unknown") {
                    emit("error");
                    console.log("unknow errror");
                    submitting.value = false;
                }
            },
            {
                ...params,
                response_type: "code", // must be set to 'code' for System User access token
                override_default_response_type: true, // when true, any response types passed in the "response_type" will take precedence over the default types
                extras: {
                    setup: {},
                    sessionInfoVersion: "3",
                    featureType: "only_waba_sharing", // Bypass phone number selection
                },
            },
        );

        return;
    }

    if (key === "google") {
        googleSdkLoaded((google) => {
            console.log(props.connection);
            const client = google.accounts.oauth2.initCodeClient({
                client_id: props.connection.config.client_id,
                redirect_uri: props.connection.config.redirect_uri,
                scope: "email profile openid https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive",
                ux_mode: "redirect",
                callback: (response) => {
                    console.log("Handle the response", response);
                },
                error_callback: (error) => {
                    console.error("OAuth error:", error);
                },
            });
            client.requestCode();
        });

        return;
    }

    const fullUrl = new URL(url);

    if (Object.keys(params)) {
        for (const key of Object.keys(params)) {
            fullUrl.searchParams.set(key, params[key]);
        }
    }

    submitting.value = true;
    const popupWindow = usePopup(fullUrl.href, key, {
        resizable: true,
        scrollbars: true,
        status: true,
        width: Math.round(window.outerWidth * 0.5),
        height: Math.round(window.outerHeight * 0.75),
    });

    function finalized() {
        emit("connected");
        if (props.connected) {
            props.connected();
        }
        submitting.value = false;
        window?.removeEventListener("finalized", finalized, true);
        clearInterval(interval.value);
        interval.value = undefined;
        popupWindow?.close();
    }

    window?.addEventListener("finalized", finalized, true);

    interval.value = setInterval(() => {
        if (popupWindow?.closed) {
            emit("error");
            clearInterval(interval.value);
            interval.value = undefined;
        }
    }, 250);
}

onBeforeUnmount(() => {
    if (interval.value) {
        clearInterval(interval.value);
        interval.value = undefined;
    }
});
</script>

<template>
    <div v-if="connection" class="flex flex-col gap-3">
        <h2 class="font-medium flex items-center justify-center gap-1.5 mb-2.5">
            <img :src="logo" class="h-8" />
        </h2>

        <UForm
            v-if="connection.type === 'credentials'"
            class="gap-3 flex flex-col"
            @submit="
                ['afas', 'recruitee'].includes(props.uuid as string)
                    ? processKombo()
                    : saveCredentials()
            "
        >
            <UFormGroup
                v-for="field in props.connection?.fields"
                :key="field.name"
                :error="
                    error.errors?.[field.name]
                        ? error.errors?.[field.name][0]
                        : undefined
                "
                :help="field.hint"
                :label="field.label"
                :required="field.required ?? true"
                size="sm"
            >
                <UInput
                    v-model="fields[field.name]"
                    :name="field.name"
                    :placeholder="field.placeholder"
                    :type="field.type"
                />
            </UFormGroup>
            <UButton
                v-if="props.connection.urls.external"
                :to="props.connection.urls.external.value"
                block
                color="gray"
                target="_blank"
            >
                {{ props.connection.urls.external.label }}
                <FontAwesomeIcon :icon="faArrowUpRightFromSquare" />
            </UButton>
            <UButton
                id="connectBtn"
                :disabled="submitting"
                :loading="submitting"
                block
                type="submit"
            >
                {{ $t("buttons.connect") }}
            </UButton>
        </UForm>
        <UForm
            v-else-if="connection.type === 'oauth-credentials'"
            class="gap-3 flex flex-col"
            @submit="saveCredentials"
        >
            <UFormGroup
                v-for="field in props.connection.fields"
                :key="field.name"
                :error="
                    error.errors?.[field.name]
                        ? error.errors?.[field.name]![0]
                        : undefined
                "
                :help="field.hint"
                :label="field.label"
                :required="field.required ?? true"
                size="sm"
            >
                <UInput
                    v-model="fields[field.name]"
                    :name="field.name"
                    :placeholder="field.placeholder"
                    :type="field.type"
                />
            </UFormGroup>

            <UButton
                id="connectBtn"
                :disabled="submitting"
                :loading="submitting"
                block
                @click="
                    oAuthConnect(
                        connection.urls.redirect,
                        connection.type,
                        fields,
                    )
                "
            >
                {{ $t("buttons.connect") }}
            </UButton>
        </UForm>
        <div v-else class="gap-3 flex flex-col">
            <UButton
                id="connectBtn"
                :disabled="submitting"
                :loading="submitting"
                block
                @click="
                    oAuthConnect(
                        connection?.urls?.redirect,
                        connection.type,
                        connection.config ?? {},
                    )
                "
            >
                {{ $t("buttons.connect") }}
            </UButton>
        </div>
    </div>
</template>
